import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  APP_ZONES,
  BaseCssProperties,
  Constants,
  DatePickerTypes,
  DropdownMenuDirection,
  FormFieldConfig,
  ProviderClass,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';

import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  checkMinAgeValidation,
  validDayOfMonthYearValidator,
} from '@app/finxone-web-frontend/app/lib/utils/validators/validators.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Store } from '@ngxs/store';
import { Subject, debounceTime, map, takeUntil } from 'rxjs';
import { StyleServiceService } from '../../../../../lib/services/style-service/style-service.service';
import { formatNameAsUrl } from '../../../../../lib/utils/zone-url.utils';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { defaultValidationMessages } from '../../../input-field-widget/default-validation-messages';
import { FormSubmitter } from '../form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-birthday-widget',
  templateUrl: './birthday-widget.component.html',
  styleUrls: ['./birthday-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BirthdayWidgetComponent extends FormSubmitter implements OnInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  @ViewChild('dateGroup') private dateGroupRef?: ElementRef<HTMLElement>;
  public zone = formatNameAsUrl(APP_ZONES.ONBOARDING);
  public widgetConfig: UiZoneWidgetConfig;
  public birthdayForm: FormGroup;
  public inputFieldsDateForm: FormGroup;
  public maxDate: Date = new Date();
  public minAge = 0;
  public isError = false;
  public borderStyle: baseCssConfigStyle;
  public allMonths = Constants.allMonths;
  public selectedMonth: number;
  public selectedDay: number;
  public selectedYear: number;
  public maxYear = new Date().getFullYear();
  public minYear = 1900;
  public isBirthdayShowSuccessIcon = false;
  public isBirthdayShowErrorIcon = false;
  public formId: string;
  public isValueChanged = false;
  public selectedMonthValue: { name: string; key: number } | undefined;
  public datePickerTypes = DatePickerTypes;
  public dateOfBirthFormControlName: string;
  public dateType: DatePickerTypes;
  public disableAutocomplete = false;
  public validationMessages = defaultValidationMessages;
  public maxDays = 31;
  public customValidationMessages: { [key: string]: string } | undefined = {};
  public isScrollable = true;
  public isScrollPeekEnabled = false;
  public visibleItemsCount = 5;
  public menuDirection = DropdownMenuDirection.AUTO;
  public readonly dateOfBirthLabel = 'Date of birth';
  // Day/Month/Year are three inputs but a single logical control, so touched state is tracked for
  // the group rather than per input.
  public isDateGroupTouched = false;

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<any>,
    protected override store: Store,
    private styleService: StyleServiceService,
    public pageConfig: PageConfigurationProvider,
    private workflowFormsService: WorkflowFormsService,
    private zonePageUrlProvider: ZonePageUrlProvider,
  ) {
    super(store);
  }

  ngOnInit(): void {
    this.formId = this.dataIn?.attri?.widgetProperties?.parentForm;
    this.dateType = this.dataIn?.attri?.widgetProperties?.dateType ?? DatePickerTypes.INPUT;
    if (this.dataIn != undefined && this.dataIn != null) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
    this.minAge = this.widgetConfig.attributes?.['field_attributes'][0]?.decision_config?.minimum_age ?? 18;
    // Should disable auto complete on the form field in the browser
    this.disableAutocomplete =
      this.widgetConfig.attributes.widgetProperties?.['disableAutocomplete'] ?? false;
    this.customValidationMessages =
      this.widgetConfig?.attributes.widgetProperties?.inputFieldConfig?.validationMessages;
    this.setMonthDropdownConfig();
    this.getWidgetCssConfig();
    this.buildForm();

    this.checkBirthdaySuccessErrorIconFlag();
    this.handleDateOfBirthChanges();
    this.getSelectedMonthName();
  }

  /**
   * Applies the configured scroll & layout settings to the month dropdown.
   */
  private setMonthDropdownConfig(): void {
    const inputFieldConfig = this.widgetConfig?.attributes.widgetProperties?.inputFieldConfig;

    this.isScrollable = inputFieldConfig?.isScrollable ?? true;
    this.isScrollPeekEnabled = inputFieldConfig?.isScrollPeekEnabled ?? false;
    this.visibleItemsCount = inputFieldConfig?.visibleItemsCount ?? 5;
    this.menuDirection = inputFieldConfig?.menuDirection ?? DropdownMenuDirection.AUTO;
  }

  private handleDateOfBirthChanges(): void {
    if (this.dateType === this.datePickerTypes.INPUT) {
      this.inputFieldsDateOfBirthChanges();
    } else if (this.dateType === this.datePickerTypes.DATEPICKER) {
      this.dateOfBirthDatePickerChanges();
    }
  }

  /**
   * Listens to changes in the input fields form (day, month, year)
   * and updates the main birthday form's 'dob' field accordingly.
   * Performs basic validation and marks the step as valid/invalid.
   */
  private inputFieldsDateOfBirthChanges(): void {
    this.inputFieldsDateForm?.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((dateFormValues) => {
        // If form is invalid, reset DOB and mark step as invalid
        if (this.inputFieldsDateForm.invalid) {
          this.birthdayForm?.get('dob')?.setValue(null);
          this.isError = true;
          return;
        }

        // Construct new UTC date from input fields
        const newBirthDate = this.normalizeToUTC(
          new Date(dateFormValues.year, dateFormValues.month, dateFormValues.day),
        );

        const currentBirthDate = this.birthdayForm?.get('dob')?.value;

        // Update birthday form only if date has changed
        if (!currentBirthDate || new Date(currentBirthDate).getTime() !== newBirthDate.getTime()) {
          this.birthdayForm?.get('dob')?.setValue(newBirthDate);
          this.isError = false;
        }
      });
  }

  /**
   * Listens to changes in the date picker control ('dob') and updates
   * the form state accordingly. Normalizes the date to UTC and validates age.
   */
  private dateOfBirthDatePickerChanges(): void {
    this.birthdayForm
      ?.get('dob')
      ?.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((dob: Date) => {
        // If dob is invalid, mark step as invalid
        if (!dob || !(dob instanceof Date)) {
          this.isError = true;
          return;
        }

        // Normalize date to UTC to avoid timezone issues
        const normalizedUTCDate = this.normalizeToUTC(dob);
        this.birthdayForm.get('dob')?.setValue(normalizedUTCDate, { emitEvent: false });

        const age = this.getAge(normalizedUTCDate);

        // Validate age against minimum requirement
        if (!this.hasRequiredAge(age)) {
          this.isError = true;
        } else {
          this.isError = false;
        }
      });
  }

  public normalizeToUTC(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  }

  /**
   * Calculates age in years from a birth date.
   * @param birthDate - The birth date (assumed UTC)
   * @returns Age in full years
   */
  private getAge(birthDate: Date): number {
    const today = new Date();
    const age = today.getUTCFullYear() - birthDate.getUTCFullYear();
    return today.getTime() <
      new Date(today.getUTCFullYear(), birthDate.getUTCMonth(), birthDate.getUTCDate()).getTime()
      ? age - 1
      : age;
  }

  private hasRequiredAge(age: number): boolean {
    return age >= this.minAge;
  }

  /**
   * Gets validation message from config if available, otherwise returns default message
   * @param messageKey - The key to look up custom message (e.g., 'dayRequired', 'dayRange')
   * @param defaultMessage - The default message to use as fallback
   * @returns The custom message if available, otherwise the default message
   */
  private getValidationMessage(messageKey: string, defaultMessage: string): string {
    return this.customValidationMessages?.[messageKey] ?? defaultMessage;
  }

  /**
   * Marks the date of birth as touched once focus leaves the Day/Month/Year group as a whole.
   * Moving between the three parts, or into the month dropdown list (which is appended to the
   * body and so sits outside the group in the DOM), keeps the group untouched.
   */
  public onDateGroupFocusOut(event: FocusEvent, isMonthDropdownOpen = false): void {
    if (isMonthDropdownOpen) {
      return;
    }

    const nextFocused = event.relatedTarget as Node | null;
    if (nextFocused && this.dateGroupRef?.nativeElement?.contains(nextFocused)) {
      return;
    }

    this.isDateGroupTouched = true;
  }

  public getInputDateError(inputFieldsDateForm: FormGroup): string | null {
    if (!inputFieldsDateForm || !this.isDateGroupTouched) {
      return null;
    }

    const year = inputFieldsDateForm?.get('year');
    const month = inputFieldsDateForm?.get('month');
    const day = inputFieldsDateForm?.get('day');

    // Any missing part reports a single message for the date rather than one per input
    if (day?.errors?.['required'] || month?.errors?.['required'] || year?.errors?.['required']) {
      const defaultMsg = defaultValidationMessages.required(this.dateOfBirthLabel);
      return this.getValidationMessage('dobRequired', defaultMsg);
    }

    if (day?.errors?.['max'] || day?.errors?.['min']) {
      const defaultMsg = defaultValidationMessages.range('Day', 1, this.maxDays);
      return this.getValidationMessage('dayRange', defaultMsg);
    }

    // Checks year validation errors (min/max limits or minlength)
    if (year?.errors?.['min'] || year?.errors?.['max'] || inputFieldsDateForm.errors?.['minlength']) {
      const defaultMsg = defaultValidationMessages.range('Year', this.minYear, this.maxYear);
      return this.getValidationMessage('yearRange', defaultMsg);
    }

    // Check for general form-level errors (e.g., invalid combination of day/month/year, minimum age)
    if (inputFieldsDateForm.errors?.['error'] && !year?.errors?.['min'] && !year?.errors?.['max']) {
      return inputFieldsDateForm.errors['error'];
    }

    return null; // No error
  }

  private getWidgetCssConfig() {
    const styles = this.styleService.generateStyles(this.attri);
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);
    this.borderStyle = {
      ...styles.borderStyle,
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.BORDER,
        BaseCssProperties.BACKGROUND_COLOR,
      ]),
    };
  }

  private checkBirthdaySuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isBirthdayShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isBirthdayShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }

  public createInputFieldsDateForm() {
    this.inputFieldsDateForm = this.createInputAgeDatePickerForm(
      this.selectedDay,
      this.selectedMonth,
      this.selectedYear,
      this.minYear,
      this.maxYear,
      this.minAge,
    );
    this.initDayValidationListeners();
  }

  get day() {
    return this.inputFieldsDateForm?.get('day');
  }
  get year() {
    return this.inputFieldsDateForm?.get('year');
  }

  private buildForm() {
    if (!this.formId) return;

    const formConfig: FormFieldConfig[] = (this.widgetConfig?.attributes['field_attributes'] || []).map(
      (field: any) => {
        return {
          id: field.name,
          type: field.type,
          label: field.label || field.name,
          parentForm: this.formId,
          name: field.name,
          validation: {
            required: field.decision_config?.required ?? false,
            minAge: this.minAge,
            minYear: this.minYear,
            maxYear: this.maxYear,
          },
        };
      },
    );
    const formId = this.workflowFormsService.generateCustomFormId(this.formId, this.zonePageUrlProvider);
    this.birthdayForm = this.workflowFormsService.getFormPart(formId) ?? new FormGroup({});
    this.workflowFormsService.addFormControl(this.birthdayForm, formConfig);

    const stateValues =
      this.store.selectSnapshot(FormActionState.getFormActionStateWithId(this.formId)) ?? {};

    if (stateValues?.formData?.['dob']) {
      const dob = new Date(stateValues?.formData?.['dob']);
      this.birthdayForm.patchValue({ dob: dob });
      this.setInputDatePickerValue();
    } else {
      this.birthdayForm.patchValue({ dob: null });
    }
    this.subscribeToDobChanges();

    this.createInputFieldsDateForm();
    this.isError = false;
  }

  private subscribeToDobChanges() {
    this.store
      .select(FormActionState.getFormActionStateWithId(this.formId))
      .pipe(
        takeUntil(this.destroy$),
        map((state) => state?.formData?.dob),
      )
      .subscribe((dob) => {
        const inputFieldDobValue = this.normalizeToUTC(
          new Date(
            this.inputFieldsDateForm?.value?.year,
            this.inputFieldsDateForm?.value?.month,
            this.inputFieldsDateForm?.value?.day,
          ),
        );
        if (dob && inputFieldDobValue !== this.birthdayForm?.value?.dob) {
          this.syncDobToInputFields(this.birthdayForm.value.dob);
        }
      });
  }

  private syncDobToInputFields(dob: Date | null): void {
    if (!dob || !this.inputFieldsDateForm) return;

    const date = new Date(dob);

    this.inputFieldsDateForm.patchValue(
      {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
      },
      { emitEvent: false },
    );
    this.getSelectedMonthName();
  }

  private setInputDatePickerValue() {
    this.selectedYear = new Date(this.birthdayForm.value['dob']).getFullYear();
    this.selectedDay = new Date(this.birthdayForm.value['dob']).getDate();
    this.selectedMonth = new Date(this.birthdayForm.value['dob']).getMonth();
  }

  private getSelectedMonthName() {
    const monthValue = this.inputFieldsDateForm?.get('month')?.value;
    this.selectedMonthValue = this.allMonths?.find((month) => month.key === monthValue);
  }

  private createInputAgeDatePickerForm(
    selectedDay: number,
    selectedMonth: number,
    selectedYear: number,
    minYear: number,
    maxYear: number,
    minAge: number,
  ): FormGroup {
    return new FormGroup(
      {
        day: new FormControl(selectedDay, [Validators.required]),
        month: new FormControl(selectedMonth, [Validators.required]),
        year: new FormControl(selectedYear, [
          Validators.required,
          Validators.min(minYear),
          Validators.max(maxYear),
        ]),
      },
      [validDayOfMonthYearValidator('year', 'month', 'day'), checkMinAgeValidation(minAge)],
    );
  }

  private initDayValidationListeners(): void {
    this.inputFieldsDateForm
      ?.get('month')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDayValidator());

    this.inputFieldsDateForm
      ?.get('year')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDayValidator());

    this.updateDayValidator();
  }

  private updateDayValidator(): void {
    if (!this.inputFieldsDateForm) return;

    const month = this.inputFieldsDateForm?.get('month')?.value;
    const year = this.inputFieldsDateForm?.get('year')?.value;
    const dayControl = this.inputFieldsDateForm?.get('day');

    this.maxDays = new Date(year, month + 1, 0).getDate();

    dayControl?.setValidators([Validators.required, Validators.min(1), Validators.max(this.maxDays)]);

    dayControl?.updateValueAndValidity({ emitEvent: false });
  }
}
